import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { navigateToTerminalCommand } from '@/lib/navigation'
import { addToWatchlist } from '@/lib/api'
import { clsx } from 'clsx'

interface TickerLinkProps {
  ticker: string
  className?: string
  children?: React.ReactNode
}

const MENU_ITEMS = [
  { label: 'Stock Detail', action: 'detail' },
  { label: 'Chart', action: 'chart' },
  { label: 'Quote', action: 'quote' },
  { label: 'Technicals', action: 'technicals' },
  { label: 'Add to Watchlist', action: 'watchlist' },
  { label: 'Analyze', action: 'analyze' },
] as const

export default function TickerLink({ ticker, className, children }: TickerLinkProps) {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 })
  const menuRef = useRef<HTMLDivElement>(null)
  const linkRef = useRef<HTMLSpanElement>(null)

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/app/stock/${ticker}`)
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuPos({ x: e.clientX, y: e.clientY })
    setMenuOpen(true)
  }

  const handleMenuAction = useCallback((action: string) => {
    setMenuOpen(false)
    switch (action) {
      case 'detail':
        navigate(`/app/stock/${ticker}`)
        break
      case 'chart':
        navigate(`/app/chart?ticker=${ticker}`)
        break
      case 'quote':
        navigateToTerminalCommand(navigate, `/quote ${ticker}`)
        break
      case 'technicals':
        navigateToTerminalCommand(navigate, `/technicals ${ticker}`)
        break
      case 'watchlist':
        addToWatchlist({ ticker, name: ticker }).catch(() => {})
        break
      case 'analyze':
        navigateToTerminalCommand(navigate, `/analyze ${ticker}`)
        break
    }
  }, [navigate, ticker])

  // Close menu on click outside
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  // Close on Escape
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [menuOpen])

  return (
    <>
      <span
        ref={linkRef}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        className={clsx(
          'font-mono text-cyan-400 cursor-pointer hover:underline hover:text-cyan-300 transition-colors',
          className,
        )}
        role="link"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter') handleClick(e as unknown as React.MouseEvent) }}
      >
        {children || ticker}
      </span>

      {menuOpen && (
        <div
          ref={menuRef}
          className="fixed z-[9999] min-w-[160px] py-1 bg-surface-card border border-border rounded-lg shadow-xl animate-fade-in"
          style={{
            left: menuPos.x,
            top: menuPos.y,
          }}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-muted font-semibold border-b border-border">
            {ticker}
          </div>
          {MENU_ITEMS.map(item => (
            <button
              key={item.action}
              onClick={() => handleMenuAction(item.action)}
              className="w-full text-left px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  )
}
